import { Button, type ButtonVariant } from "@/components/ui/Button";

import {
  AlertAction,
  AlertCancel,
  AlertContent,
  AlertDescription,
  AlertOverlay,
  AlertPortal,
  AlertRoot,
  AlertTitle,
  AlertTrigger,
} from "./AlertDialogComponents";

type AlertDialogProps = {
  triggerElement?: React.ReactNode;
  title: string;
  body: string;
  cancelLabel: string;
  actionLabel: string;
  onAction: () => void;
  onCancel: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  isControlled: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  actionButtonVariant?: ButtonVariant;
  cancelButtonVariant?: ButtonVariant;
};
/**
 *
 * @param triggerElement The element that will trigger the alert dialog,
 * if not provided a default button will be used. Controlled alert dialogs do not use this prop
 * @param title The title of the alert dialog
 * @param body The body of the alert dialog
 * @param cancelLabel The label for the cancel button
 * @param actionLabel The label for the action button
 * @param onAction The function to be called when the action button is clicked
 * @param onCancel The function to be called when the cancel button is clicked
 * @param onOpen The function to be called when the alert dialog is opened.
 * This is only triggerd for uncontrolled alert dialogs
 * @param onClose The function to be called when the alert dialog is closed.
 * This is only triggerd for uncontrolled alert dialogs
 * @param isControlled Whether the alert dialog is controlled or not
 * @param isOpen The state of the alert dialog when it is controlled.
 * Only used when the alert dialog is controlled
 * @param onOpenChange The function to be called when the state of the alert dialog changes.
 * Only used when the alert dialog is controlled
 * @returns A controlled or uncontrolled Alert Dialog
 */
const AlertDialog = ({
  triggerElement,
  title,
  body,
  cancelLabel,
  actionLabel,
  onAction = () => {},
  onCancel = () => {},
  onOpen = () => {},
  onClose = () => {},
  isControlled = false,
  isOpen = undefined,
  onOpenChange = undefined,
  actionButtonVariant = "primary",
  cancelButtonVariant = "secondary",
}: AlertDialogProps) => (
  <AlertRoot
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    isControlled={isControlled}
    onClose={onClose}
    onOpen={onOpen}
  >
    {!isControlled && (
      <AlertTrigger asChild>
        {triggerElement ?? (
          <Button size="sm" variant="primary">
            Open
          </Button>
        )}
      </AlertTrigger>
    )}
    <AlertPortal>
      <AlertOverlay />
      <AlertContent>
        <AlertTitle className="text-heading-lg py-0 my-0 text-primary">
          {title}
        </AlertTitle>
        <AlertDescription className="text-body-sm text-primary">
          {body}
        </AlertDescription>
        <div className="mt-2 flex gap-4 align-middle justify-end">
          <AlertCancel>
            <Button onClick={onCancel} size="sm" variant={cancelButtonVariant}>
              {cancelLabel}
            </Button>
          </AlertCancel>
          <AlertAction>
            <Button onClick={onAction} size="sm" variant={actionButtonVariant}>
              {actionLabel}
            </Button>
          </AlertAction>
        </div>
      </AlertContent>
    </AlertPortal>
  </AlertRoot>
);

export { AlertDialog };
